import {driver} from "driver.js";
import "driver.js/dist/driver.css";

// Reference: https://driverjs.com/docs/configuration
const STATE_KEY = "minerva.activeTour";

let pageData = null;
let driverObj = null;
let leavingPage = false;

const readState = () => JSON.parse(sessionStorage.getItem(STATE_KEY) || "null");
// returnUrl: the real page a demo tour was started from, where closing the tour brings the user back
const writeState = (key, index, returnUrl) => {
    const current = readState();
    sessionStorage.setItem(STATE_KEY, JSON.stringify({
        key, index, returnUrl: returnUrl ?? (current?.key === key ? current.returnUrl : undefined),
    }));
};
const clearState = () => sessionStorage.removeItem(STATE_KEY);

// Demo modals (see TourDemoMixin) come with the page, keyed by the URL that would load them: the demo entries no
// longer exist by the time they would be requested
const demoModalTemplate = (url) => url && document.querySelector(`template[data-tour-modal="${CSS.escape(url)}"]`);
const stepModalTemplate = (step) => step.modal && demoModalTemplate(pageData.modals[step.modal]);

// The demo content a modal would load on its own once open: matched by path, since the modal's own URLs also carry
// query strings
const loadedDemoTemplate = (url) => {
    if (!url) {
        return null;
    }
    const requested = new URL(url, window.location.origin);
    return [...document.querySelectorAll("template[data-tour-modal]")].find(
        (template) => new URL(template.dataset.tourModal, window.location.origin).pathname === requested.pathname
    );
};

const modalInstance = () => window.bootstrap.Modal.getOrCreateInstance(
    // No focus trap: it would take the focus away from the tour's popover
    document.getElementById("modal"), {focus: false}
);

const showDemoModal = (template) => new Promise((resolve) => {
    const modalElement = document.getElementById("modal");
    modalElement.innerHTML = template.innerHTML;
    // Content the modal would load on its own, e.g. the working orders to choose from
    modalElement.querySelectorAll('[hx-get][hx-trigger~="load"]').forEach((element) => {
        const content = loadedDemoTemplate(element.getAttribute("hx-get"));
        const target = element.getAttribute("hx-target");
        if (content && target) {
            document.querySelector(target).innerHTML = content.innerHTML;
        }
    });
    modalElement.addEventListener("shown.bs.modal", resolve, {once: true});
    modalInstance().show();
});

const hideDemoModal = () => new Promise((resolve) => {
    const modalElement = document.getElementById("modal");
    if (!modalElement.classList.contains("show")) {
        resolve();
        return;
    }
    modalElement.addEventListener("hidden.bs.modal", resolve, {once: true});
    modalInstance().hide();
});

// Collapsed elements or dropdown menus a step expands (e.g. the advanced filters), collapsed back afterwards unless
// they were already open
let expandedByTour = null;

// Bootstrap component, its "shown" event and its open state for the element with that id
const expandable = (id) => {
    const element = document.getElementById(id);
    if (!element) {
        return null;
    }
    if (element.matches('[data-bs-toggle="dropdown"]')) {
        // Not closed by clicks elsewhere, e.g. on the tour's own buttons
        return {
            component: window.bootstrap.Dropdown.getOrCreateInstance(element, {autoClose: false}),
            element, event: "dropdown", isOpen: element.classList.contains("show"),
        };
    }
    return {
        component: window.bootstrap.Collapse.getOrCreateInstance(element, {toggle: false}),
        element, event: "collapse", isOpen: element.classList.contains("show"),
    };
};

const expand = (id) => new Promise((resolve) => {
    const target = expandable(id);
    if (!target || target.isOpen) {
        resolve();
        return;
    }
    expandedByTour = id;
    target.element.addEventListener(`shown.bs.${target.event}`, resolve, {once: true});
    target.component.show();
});

const collapse = (id) => new Promise((resolve) => {
    const target = expandable(id);
    if (!target || expandedByTour !== id || !target.isOpen) {
        resolve();
        return;
    }
    expandedByTour = null;
    target.element.addEventListener(`hidden.bs.${target.event}`, resolve, {once: true});
    target.component.hide();
});

// What a step shows before being highlighted: its demo modal or its expanded element
const reveal = (step) => {
    if (step.modal) {
        return showDemoModal(stepModalTemplate(step));
    }
    return step.expand ? expand(step.expand) : Promise.resolve();
};
const conceal = (step) => {
    if (step.modal) {
        return hideDemoModal();
    }
    return step.expand ? collapse(step.expand) : Promise.resolve();
};

const selectorFor = (step) => {
    if (step.modal) {
        return step.target ? `#modal [data-tour="${step.target}"]` : "#modal .modal-content";
    }
    return step.target ? `[data-tour="${step.target}"]` : undefined;
};

// Whether a step is shown on this page, in the state of the demo it needs (e.g. the plan once scheduled) and with the
// query parameters it needs (e.g. another view of the same list)
const isHere = (step) => step.page === pageData.page && (step.state || null) === (pageData.state || null)
    && Object.entries(step.params).every(([key, value]) => pageData.params[key] === value);

const isOnPage = (step) => isHere(step) && (
    step.modal ? Boolean(stepModalTemplate(step)) : !step.target || document.querySelector(selectorFor(step))
);

// Feedback while the next page loads, which for demo pages includes preparing the demo entries
const navigate = (url, text) => {
    const modal = document.getElementById("tourLoadingModal");
    if (modal) {
        document.getElementById("tourLoadingText").textContent = text;
        window.bootstrap.Modal.getOrCreateInstance(modal).show();
    }
    window.location.href = url;
};

// Coming back with the browser's back button may restore the page with the loading modal still open
window.addEventListener("pageshow", (e) => {
    const modal = document.getElementById("tourLoadingModal");
    if (e.persisted && modal) {
        window.bootstrap.Modal.getOrCreateInstance(modal).hide();
    }
});

// The page the tour was started from, or the tour's own exit page if it was opened directly on a demo page
const exitUrl = () => readState()?.returnUrl || pageData.exitUrl;

const endTour = () => {
    const url = exitUrl();
    clearState();
    if (pageData.demo) {
        // Back to a real page, without the demo entries
        navigate(url, "Closing the tour, one moment...");
    }
};

// Moves to tour.steps[index], which lives on another page: navigates there if it has its own URL, otherwise the
// previous step's text tells the user where to go and the tour resumes once they get there.
const leaveForStep = (tour, index) => {
    writeState(tour.key, index);
    leavingPage = true;
    driverObj.destroy();
    const step = tour.steps[index];
    if (step.url) {
        let text = "Opening the next page of the tour, one moment...";
        if (step.page === pageData.page) {
            text = pageData.demo ? "Updating the example, one moment..." : "Switching the view, one moment...";
        }
        navigate(step.url, text);
    }
};

const goToStep = (tour, index) => {
    if (index < 0) {
        return;
    }
    if (index >= tour.steps.length) {
        driverObj.destroy();
        return;
    }
    leaveForStep(tour, index);
};

const sameParams = (step, other) => JSON.stringify(step.params) === JSON.stringify(other.params);

// What moving from `step` to `next` does, for the button leading there
const nextButtonText = (step, next) => {
    if (!next) {
        return "Done";
    }
    if (next.page !== step.page) {
        return "Go to the next page →";
    }
    if (!sameParams(step, next)) {
        return "Switch the view →";
    }
    if ((next.state || null) !== (step.state || null)) {
        // The example page after the action the step describes, e.g. the plan once the dates are set
        return "See the result →";
    }
    if (next.modal && next.modal !== step.modal) {
        return step.modal ? "Open the next window →" : "Open the window →";
    }
    if (step.modal && !next.modal) {
        return "Close the window →";
    }
    if (next.expand !== step.expand) {
        const element = document.getElementById(next.expand || step.expand);
        const kind = element?.matches('[data-bs-toggle="dropdown"]') ? "menu" : "panel";
        return next.expand ? `Open the ${kind} →` : `Close the ${kind} →`;
    }
    return "Next →";
};

const startTour = (tour, startIndex = 0) => {
    if (tour.demo && pageData.demo !== tour.key) {
        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.delete("tour");
        writeState(tour.key, startIndex, pageData.demo ? undefined : returnUrl.pathname + returnUrl.search);
        navigate(tour.steps[startIndex].url, "Preparing the tour with example data, one moment...");
        return;
    }
    // Started from another view of the page, e.g. the calendar for a tour beginning on the job view
    const startStep = tour.steps[startIndex];
    if (!isHere(startStep)) {
        if (startStep.url) {
            writeState(tour.key, startIndex);
            navigate(startStep.url, "Switching the view, one moment...");
        }
        return;
    }
    // The run of consecutive steps from startIndex that are on this page; steps whose target isn't rendered for this
    // user (e.g. a button they have no permission for) are skipped.
    const indexes = [];
    let lastIndex = startIndex;
    for (let index = startIndex; index < tour.steps.length && isHere(tour.steps[index]); index++) {
        lastIndex = index;
        if (isOnPage(tour.steps[index])) {
            indexes.push(index);
        }
    }
    if (!indexes.length) {
        return;
    }

    if (driverObj) {
        leavingPage = true;
        driverObj.destroy();
    }
    leavingPage = false;
    writeState(tour.key, indexes[0]);

    // The step each one moves on to: the next one shown here, otherwise the one after this page's last step
    const nextStep = (position) => tour.steps[position + 1 < indexes.length ? indexes[position + 1] : lastIndex + 1];

    // Shows what the next step needs, and hides what the current one showed, before moving: the step is highlighted
    // once in place
    const moveWithin = (toActiveIndex, move) => {
        const from = tour.steps[indexes[driverObj.getActiveIndex()]];
        const to = tour.steps[indexes[toActiveIndex]];
        if (from.modal === to.modal && from.expand === to.expand) {
            move();
        } else {
            conceal(from).then(() => reveal(to)).then(move);
        }
    };

    driverObj = driver({
        // Wider than Driver.js' default, for texts written for first-time users
        popoverClass: "minerva-tour",
        showProgress: true,
        allowClose: true,
        overlayClickBehavior: "none",
        // Tours only show how the page behaves: not even the highlighted element can be used
        disableActiveInteraction: true,
        steps: indexes.map((index, position) => ({
            element: selectorFor(tour.steps[index]),
            popover: {
                title: tour.steps[index].title,
                description: tour.steps[index].text,
                progressText: `${index + 1} of ${tour.steps.length}`,
                showButtons: ["next", "previous", "close"],
                disableButtons: index === 0 ? ["previous"] : [],
                // What comes next is never a surprise: the button says so
                nextBtnText: nextButtonText(tour.steps[index], nextStep(position)),
                doneBtnText: nextButtonText(tour.steps[index], nextStep(position)),
            },
        })),
        onHighlighted: () => writeState(tour.key, indexes[driverObj.getActiveIndex()]),
        onNextClick: () => {
            if (driverObj.hasNextStep()) {
                moveWithin(driverObj.getActiveIndex() + 1, () => driverObj.moveNext());
            } else if (lastIndex + 1 >= tour.steps.length) {
                driverObj.destroy();
            } else {
                goToStep(tour, lastIndex + 1);
            }
        },
        onPrevClick: () => {
            if (driverObj.hasPreviousStep()) {
                moveWithin(driverObj.getActiveIndex() - 1, () => driverObj.movePrevious());
            } else {
                goToStep(tour, startIndex - 1);
            }
        },
        onDestroyed: () => {
            if (!leavingPage) {
                endTour();
            }
            driverObj = null;
        },
    });
    reveal(tour.steps[indexes[0]]).then(() => driverObj.drive());
};

// The tour's first step on this page, whatever view or demo state it needs: startTour gets the page there
const firstIndexOnPage = (tour) => Math.max(tour.steps.findIndex((step) => step.page === pageData.page), 0);

const initTours = () => {
    const element = document.getElementById("tours-data");
    pageData = element ? JSON.parse(element.textContent) : null;
    if (!pageData) {
        return;
    }
    const toursByKey = Object.fromEntries(pageData.tours.map((tour) => [tour.key, tour]));

    // Resumes where the tour was left, or at the next step when the user followed the previous step's instructions
    // to get to this page
    const state = readState();
    const resumedTour = state && toursByKey[state.key];
    const resumeIndex = resumedTour && [state.index, state.index + 1].find(
        (index) => resumedTour.steps[index] && isHere(resumedTour.steps[index])
    );
    if (resumeIndex !== undefined && resumeIndex !== null) {
        startTour(resumedTour, resumeIndex);
        return;
    }
    if (resumedTour) {
        clearState();
    }

    const requestedTour = toursByKey[new URLSearchParams(window.location.search).get("tour")];
    if (requestedTour) {
        startTour(requestedTour, firstIndexOnPage(requestedTour));
    }
};

document.addEventListener("DOMContentLoaded", initTours);

// On demo pages, and on real ones while a tour runs, nothing may reach the server: every action is blocked, except the
// tour's own controls. Demo pages are recognized from their exit banner, so a page is never blocked without a way out
// (e.g. a stale tour_demo URL), rather than from the page data, which is only parsed once htmx may already have sent
// its on-load requests. The listeners are on window, ahead of Bootstrap's own capturing ones on document (e.g.
// data-bs-toggle="modal", which would open the modal anyway).
const isDemoPage = () => Boolean(document.querySelector("[data-tour-demo-banner]"));
const actionsBlocked = () => isDemoPage() || Boolean(driverObj?.isActive());
const isTourControl = (element) => element.closest(".driver-popover, [data-tour-exit], [data-tour-menu]");

window.addEventListener("click", (e) => {
    const action = e.target.closest("a[href], button, input[type=submit], [hx-get], [hx-post]");
    if (actionsBlocked() && action && !isTourControl(action)) {
        e.preventDefault();
        e.stopPropagation();
    }
}, true);

window.addEventListener("submit", (e) => {
    if (actionsBlocked()) {
        e.preventDefault();
        e.stopPropagation();
    }
}, true);

// Inputs navigating on change, e.g. the quick filters
window.addEventListener("change", (e) => {
    if (actionsBlocked() && !isTourControl(e.target)) {
        e.stopPropagation();
    }
}, true);

// Also the read-only ones: on demo pages the demo entries no longer exist once the page is rendered
const blockDemoRequest = (e) => {
    if (actionsBlocked()) {
        e.preventDefault();
        e.stopPropagation();
    }
};
document.body.addEventListener("htmx:confirm", blockDemoRequest);
document.body.addEventListener("htmx:beforeRequest", blockDemoRequest);

document.addEventListener("click", (e) => {
    const exitLink = e.target.closest("[data-tour-exit]");
    if (exitLink) {
        e.preventDefault();
        const url = exitUrl();
        clearState();
        navigate(url, "Closing the tour, one moment...");
        return;
    }
    const button = e.target.closest("[data-start-tour]");
    if (button && pageData) {
        const tour = pageData.tours.find((candidate) => candidate.key === button.dataset.startTour);
        if (tour) {
            startTour(tour, firstIndexOnPage(tour));
        }
    }
});

document.body.addEventListener("htmx:afterSettle", (e) => {
    if (e.detail.target === document.body) {
        // Boosted navigation: a whole new page, with its own tours. The tour state is kept so it can resume there.
        if (driverObj) {
            leavingPage = true;
            driverObj.destroy();
        }
        initTours();
    } else if (driverObj?.isActive()) {
        // The highlighted element may have been swapped out: highlight it again from the fresh DOM
        driverObj.moveTo(driverObj.getActiveIndex());
    }
});
